// Pure helpers for the team/player selection tree on the Create Rule form
// (WatcherFormTeams.tsx). The tree is conference → division → team, plus
// players under each team when building a player-prop rule.

export interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  icon?: string;
}

export type CheckedItems = Record<string, boolean>;
export type IndeterminateItems = Record<string, boolean>;

export function buildPlayersByTeam(
  playerMetadata: Record<string, unknown>,
): Record<string, TreeNode[]> {
  const result: Record<string, TreeNode[]> = {};
  Object.entries(playerMetadata).forEach(([playerId, rawData]) => {
    let teamId: string;
    let playerName: string;
    if (typeof rawData === "string" || typeof rawData === "number") {
      teamId = String(rawData);
      playerName = playerId;
    } else if (rawData && typeof rawData === "object") {
      const pd = rawData as Record<string, unknown>;
      teamId = String(pd["team_id"] ?? pd["team"] ?? "");
      playerName = (pd["display_name"] as string) || (pd["name"] as string) || playerId;
    } else {
      return;
    }
    if (!teamId) return;
    if (!result[teamId]) result[teamId] = [];
    result[teamId].push({ id: `P${playerId}`, name: playerName });
  });
  Object.values(result).forEach((players) =>
    players.sort((a, b) => a.name.localeCompare(b.name)),
  );
  return result;
}

export function augmentTreeWithPlayers(
  nodes: TreeNode[],
  playersByTeam: Record<string, TreeNode[]>,
): TreeNode[] {
  return nodes.map((node) => {
    if (node.children && node.children.length > 0) {
      return { ...node, children: augmentTreeWithPlayers(node.children, playersByTeam) };
    }
    const players = playersByTeam[String(node.id)] ?? [];
    return players.length > 0 ? { ...node, children: players } : node;
  });
}

// Get this node's id plus every descendant id (conference → division → team →
// player), used both to cascade a checkbox toggle down and to re-expand a
// pruned selection back out for display.
export function getDescendantIds(node: TreeNode): string[] {
  let ids = [node.id];
  if (node.children) {
    node.children.forEach((child) => {
      ids = [...ids, ...getDescendantIds(child)];
    });
  }
  return ids;
}

// A saved rule's `teams` list only records the highest-level id that covers a
// fully-selected branch (e.g. just the conference, not every division/team/
// player under it) — see collectMinimalSelection. To show the tree correctly
// on edit, re-expand each recorded id back into itself plus every descendant.
export function expandSelection(
  selected: Record<string, boolean>,
  nodes: TreeNode[],
): CheckedItems {
  const result: CheckedItems = {};
  const walk = (list: TreeNode[]) => {
    list.forEach((node) => {
      if (selected[node.id]) {
        getDescendantIds(node).forEach((id) => {
          result[id] = true;
        });
      } else if (node.children) {
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return result;
}

// Inverse of expandSelection: walks the tree top-down and, for the submitted
// `teams` list, keeps only the highest-level id needed to cover each fully-
// selected branch — a fully-checked conference contributes just its own id
// (not every division/team/player beneath it), same for a fully-checked
// division or (in player mode) a fully-checked team's players. Only
// indeterminate branches are descended into, to find their selected parts.
export function collectMinimalSelection(
  nodes: TreeNode[],
  checked: CheckedItems,
  indeterminate: IndeterminateItems,
): string[] {
  const result: string[] = [];
  nodes.forEach((node) => {
    if (checked[node.id]) {
      result.push(node.id);
    } else if (indeterminate[node.id] && node.children) {
      result.push(...collectMinimalSelection(node.children, checked, indeterminate));
    }
  });
  return result;
}

// Recomputes every parent's state from its children, bottom-up: a parent is
// checked when all its children are, and indeterminate when only some are
// (or some are themselves indeterminate). Leaf states are left as given.
export function recomputeTreeState(
  nodes: TreeNode[],
  checkedState: CheckedItems,
): { checked: CheckedItems; indeterminate: IndeterminateItems } {
  const indeterminate: IndeterminateItems = {};
  const checked = { ...checkedState };

  const processNodes = (list: TreeNode[]): void => {
    list.forEach((node) => {
      if (node.children && node.children.length > 0) {
        // Process children first (bottom-up approach)
        processNodes(node.children);

        const childIds = node.children.map((child) => child.id);
        const allChildrenChecked = childIds.every((id) => checked[id]);
        const someChildrenChecked = childIds.some(
          (id) => checked[id] || indeterminate[id],
        );

        checked[node.id] = allChildrenChecked;
        indeterminate[node.id] = !allChildrenChecked && someChildrenChecked;
      }
    });
  };

  processNodes(nodes);
  return { checked, indeterminate };
}
